/* istanbul ignore file */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useLayoutEffect, useEffect } from 'react';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { useApi } from '../../../hooks/use-api';
import { LookupAutocomplete } from '../components/lookup-autocomplete';
import { IUserLookup, IUserLookupProps } from './interfaces';
import { formatUserValue, renderUser } from './formatters';
import { useFetch } from './useFetch';
import { GetUserByIdResponse2 } from '../../../api-service/clients';
import { ReadonlyInput } from '../../readonly-input/readonly-input';

const lookupsClient = moduleClientFactory.createLookupUserClient();

export function UserLookup(props: IUserLookupProps) {
  const {
    onChange,
    value,
    fetchMinimumInputLength = 2,
    label = 'Bruger',
    placeholder = 'Fremsøg bruger',
    readOnly,
    teamId,
    includeInactiveUsers,
    onUserSelected,
    tooltipTitle,
    onOpenEntity,
    ...rest
  } = props;

  const [selectedValue, setSelectedValue] = useState<IUserLookup>({} as IUserLookup);
  const [searchValue, setSearchValue] = useState('');

  function handleOnChange(selected: IUserLookup) {
    onChange && onChange(selected?.userIdentifier, selected);
  }

  const lookupSingleEntityApi = useApi((cancelToken, userId) => lookupsClient.getUserById({ userId }, cancelToken), {} as GetUserByIdResponse2);

  useLayoutEffect(() => {
    if (!value) {
      setSelectedValue({} as IUserLookup);
      return;
    }
    async function getSingle() {
      const response = await lookupSingleEntityApi.invoke(value);
      if (response.result.user) {
        setSelectedValue(response.result.user);
      }
    }
    getSingle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    onUserSelected && onUserSelected(selectedValue.displayName || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  const { data, loading } = useFetch({ input: searchValue, fetchMinimumInputLength, teamId, includeInactiveUsers: !!includeInactiveUsers });

  return readOnly ? (
    <ReadonlyInput label={label} value={formatUserValue(selectedValue)} />
  ) : (
    <LookupAutocomplete<IUserLookup>
      {...rest}
      label={label}
      placeholder={placeholder}
      isOptionEqualToValue={(option, val) => option?.userIdentifier === val?.userIdentifier}
      getOptionLabel={formatUserValue}
      renderOption={renderUser}
      onChange={handleOnChange as never}
      value={selectedValue}
      onSearchValueChange={setSearchValue}
      data={data}
      isLoading={loading || lookupSingleEntityApi.isLoading}
      tooltipTitle={tooltipTitle}
      onOpenEntity={onOpenEntity}
    />
  );
}
